/**
 * ⚠️ Do not edit this file — edit `src/configs/theme.ts`.
 *
 * The whole template (layouts, header, footer, menu) reads `themeConfig` in
 * this shape. It is now just the translation of `theme.ts` into these keys, so
 * the configuration lives in one place and nothing here is kept by hand.
 */

// Type Imports
import type { Mode, Skin, Layout, LayoutComponentPosition, LayoutComponentWidth } from '@core/types'

// Config Imports
import theme from './theme'
import type { ThemeSettings } from './theme'

/**
 * The layout read through its declared type.
 *
 * `theme.ts` closes with `satisfies`, which keeps the literal shape — so a key
 * the author left out (`navbar.contentWidth`, to inherit) is simply absent
 * there. Reading it through the type is what makes the fallback below legal.
 */
const layout: ThemeSettings['layout'] = theme.layout

type Navbar = {
  type: LayoutComponentPosition
  contentWidth: LayoutComponentWidth
  floating: boolean
  detached: boolean
  blur: boolean
}

type Footer = {
  type: LayoutComponentPosition
  contentWidth: LayoutComponentWidth
  detached: boolean
}

export type Config = {
  toastPosition: 'top-right' | 'top-left' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center'
  templateName: string
  homePageUrl: string
  settingsCookieName: string
  mode: Mode
  skin: Skin
  semiDark: boolean
  layout: Layout
  layoutPadding: number
  navbar: Navbar
  contentWidth: LayoutComponentWidth
  compactContentWidth: number
  footer: Footer
  disableRipple: boolean
}

const themeConfig: Config = {
  templateName: theme.brand.name,
  homePageUrl: theme.brand.homeUrl,

  // Fixed on purpose: renaming the brand should not invalidate the cookie of
  // users already running the app with customization enabled.
  settingsCookieName: 'doxa-code-settings',

  mode: theme.appearance.mode,
  skin: theme.appearance.skin,
  semiDark: theme.appearance.semiDarkMenu,

  layout: layout.type,
  layoutPadding: layout.padding,
  contentWidth: layout.contentWidth,
  compactContentWidth: layout.compactContentWidth,

  navbar: {
    type: layout.navbar.type,
    // Follows the content unless the theme file says otherwise: one key moves
    // the whole page, instead of three that have to be kept in agreement.
    contentWidth: layout.navbar.contentWidth ?? layout.contentWidth,
    floating: layout.navbar.floating,
    detached: layout.navbar.detached,
    blur: layout.navbar.blur
  },

  footer: {
    type: layout.footer.type,
    contentWidth: layout.footer.contentWidth ?? layout.contentWidth,
    detached: layout.footer.detached
  },

  disableRipple: !theme.interaction.buttonRipple,
  toastPosition: theme.interaction.toastPosition
}

export default themeConfig
